<script>
import { format, parseISO } from 'date-fns';
import { required, url } from '@vuelidate/validators';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import HelperTextPopup from 'dashboard/components/ui/HelperTextPopup.vue';
import { isValidURL } from '../helper/URLHelper';
import { getRegexp } from 'shared/helpers/Validators';
import { useVuelidate } from '@vuelidate/core';
import { emitter } from 'shared/helpers/mitt';
import ContactAPI from 'dashboard/api/contacts';

import NextButton from 'dashboard/components-next/button/Button.vue';

const DATE_FORMAT = 'yyyy-MM-dd';

export default {
  components: {
    MultiselectDropdown,
    HelperTextPopup,
    NextButton,
  },
  props: {
    label: { type: String, required: true },
    description: { type: String, default: '' },
    values: { type: Array, default: () => [] },
    value: { type: [String, Number, Boolean], default: '' },
    showActions: { type: Boolean, default: false },
    attributeType: { type: String, default: 'text' },
    attributeRegex: {
      type: String,
      default: null,
    },
    regexCue: { type: String, default: null },
    attributeKey: { type: String, required: true },
    contactId: { type: Number, default: null },
    expiration: { type: Object, default: null },
    // For multi_entry type: the full array of entries
    entries: { type: Array, default: () => [] },
  },
  emits: ['update', 'delete', 'copy', 'expiryUpdated'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isEditing: false,
      editedValue: null,
      showExpiryPicker: false,
      expiryDateInput: '',
      // multi_entry specific
      showAddEntryForm: false,
      newEntryText: '',
      newEntryDate: '',
      newEntryLabel: '',
      newEntryAddLabel: '',
      showLabelPicker: false,
      editingEntryIndex: null,
      editingEntryText: '',
      editingEntryDate: '',
      editingEntryLabel: '',
      editingEntryAddLabel: '',
    };
  },
  computed: {
    displayValue() {
      if (this.isAttributeTypeDate) {
        return this.value
          ? new Date(this.value || new Date()).toLocaleDateString()
          : '---';
      }
      if (this.isAttributeTypeCheckbox) {
        return this.value === 'false' ? false : this.value;
      }
      return this.hasValue ? this.value : '---';
    },
    formattedValue() {
      return this.isAttributeTypeDate
        ? format(this.value ? new Date(this.value) : new Date(), DATE_FORMAT)
        : this.value;
    },
    listOptions() {
      return this.values.map((value, index) => ({
        id: index + 1,
        name: value,
      }));
    },
    selectedItem() {
      const id = this.values.indexOf(this.editedValue) + 1;
      return { id, name: this.editedValue };
    },
    isAttributeTypeCheckbox() {
      return this.attributeType === 'checkbox';
    },
    isAttributeTypeList() {
      return this.attributeType === 'list';
    },
    isAttributeTypeLink() {
      return this.attributeType === 'link';
    },
    isAttributeTypeDate() {
      return this.attributeType === 'date';
    },
    isAttributeTypeMultiEntry() {
      return this.attributeType === 'multi_entry';
    },
    hasValue() {
      return this.value !== null && this.value !== '';
    },
    formattedExpiry() {
      if (!this.expiration) return null;
      return new Date(this.expiration.expires_at).toLocaleDateString();
    },
    accountLabels() {
      return this.$store.getters['labels/getLabels'] || [];
    },
    urlValue() {
      return isValidURL(this.value) ? this.value : '---';
    },
    hrefURL() {
      return isValidURL(this.value) ? this.value : '';
    },
    notAttributeTypeCheckboxAndList() {
      return (
        !this.isAttributeTypeCheckbox &&
        !this.isAttributeTypeList &&
        !this.isAttributeTypeMultiEntry
      );
    },
    inputType() {
      return this.isAttributeTypeLink ? 'url' : this.attributeType;
    },
    shouldShowErrorMessage() {
      return this.v$.editedValue.$error;
    },
    errorMessage() {
      if (this.v$.editedValue.url) {
        return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_URL');
      }
      if (!this.v$.editedValue.regexValidation) {
        return this.regexCue
          ? this.regexCue
          : this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_INPUT');
      }
      return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.REQUIRED');
    },
  },
  watch: {
    value() {
      this.isEditing = false;
      this.editedValue = this.formattedValue;
      this.v$.$reset();
    },
    contactId() {
      // Fix to solve validation not resetting when contactId changes in contact page
      this.v$.$reset();
    },
  },

  validations() {
    if (this.isAttributeTypeLink) {
      return {
        editedValue: { required, url },
      };
    }
    return {
      editedValue: {
        required,
        regexValidation: value => {
          return !(
            this.attributeRegex && !getRegexp(this.attributeRegex).test(value)
          );
        },
      },
    };
  },
  mounted() {
    this.editedValue = this.formattedValue;
    emitter.on(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  unmounted() {
    emitter.off(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  methods: {
    onFocusAttribute(focusAttributeKey) {
      if (this.attributeKey === focusAttributeKey) {
        this.onEdit();
      }
    },
    focusInput() {
      if (this.$refs.inputfield) {
        this.$refs.inputfield.focus();
      }
    },
    onClickAway() {
      this.v$.$reset();
      this.isEditing = false;
    },
    onEdit() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.focusInput();
      });
    },
    onUpdateListValue(value) {
      if (value) {
        this.editedValue = value.name;
        this.onUpdate();
      }
    },
    onUpdate() {
      const updatedValue =
        this.attributeType === 'date'
          ? parseISO(this.editedValue)
          : this.editedValue;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.isEditing = false;
      this.$emit('update', this.attributeKey, updatedValue);
    },
    onDelete() {
      this.isEditing = false;
      this.v$.$reset();
      this.$emit('delete', this.attributeKey);
    },
    onCopy() {
      this.$emit('copy', this.value);
    },
    toggleExpiryPicker() {
      this.showExpiryPicker = !this.showExpiryPicker;
      if (this.showExpiryPicker) {
        this.expiryDateInput = this.expiration
          ? new Date(this.expiration.expires_at).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10);
      }
    },
    async onSaveExpiry() {
      if (!this.expiryDateInput || !this.contactId) return;
      try {
        const { data } = await ContactAPI.upsertAttributeExpiration(
          this.contactId,
          {
            attribute_key: this.attributeKey,
            expires_at: new Date(this.expiryDateInput).toISOString(),
            on_expire_actions: { clear_value: true },
          }
        );
        this.$emit('expiryUpdated', {
          attributeKey: this.attributeKey,
          expiration: data,
        });
        this.showExpiryPicker = false;
      } catch {
        // silent
      }
    },
    async onDeleteExpiry() {
      if (!this.expiration || !this.contactId) return;
      try {
        await ContactAPI.deleteAttributeExpiration(
          this.contactId,
          this.expiration.id
        );
        this.$emit('expiryUpdated', {
          attributeKey: this.attributeKey,
          expiration: null,
        });
        this.showExpiryPicker = false;
      } catch {
        // silent
      }
    },
    // multi_entry methods
    formatEntryDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString();
    },
    normalizeLabelValue(labelValue) {
      if (!labelValue) return '';

      const unwrap = value => {
        if (Array.isArray(value)) return unwrap(value[0]);
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
              const parsed = JSON.parse(trimmed);
              return unwrap(parsed);
            } catch {
              return trimmed;
            }
          }
          return trimmed;
        }
        return String(value);
      };

      return unwrap(labelValue);
    },
    removeEntry(index) {
      const updated = this.entries.filter((_, i) => i !== index);
      if (this.editingEntryIndex === index) {
        this.cancelEditEntry();
      } else if (
        this.editingEntryIndex !== null &&
        index < this.editingEntryIndex
      ) {
        this.editingEntryIndex -= 1;
      }
      this.$emit('update', this.attributeKey, updated.length ? updated : null);
    },
    addEntry() {
      if (!this.newEntryText.trim()) return;
      const entry = { text: this.newEntryText.trim() };
      if (this.newEntryDate) entry.expires_at = this.newEntryDate;
      if (this.newEntryLabel) entry.remove_labels = [this.newEntryLabel];
      if (this.newEntryAddLabel) entry.add_labels = [this.newEntryAddLabel];
      const updated = [...this.entries, entry];
      this.$emit('update', this.attributeKey, updated);
      this.newEntryText = '';
      this.newEntryDate = '';
      this.newEntryLabel = '';
      this.newEntryAddLabel = '';
      this.showLabelPicker = false;
      this.showAddEntryForm = false;
    },
    startEditEntry(entry, index) {
      this.editingEntryIndex = index;
      this.editingEntryText = entry.text || '';
      this.editingEntryDate = entry.expires_at || '';
      this.editingEntryLabel = this.normalizeLabelValue(entry.remove_labels);
      this.editingEntryAddLabel = this.normalizeLabelValue(entry.add_labels);
      this.showAddEntryForm = false;
    },
    saveEditedEntry(index) {
      if (index < 0 || !this.editingEntryText.trim()) return;

      const existing = this.entries[index] || {};
      const updatedEntry = {
        ...existing,
        text: this.editingEntryText.trim(),
      };

      if (this.editingEntryDate) {
        updatedEntry.expires_at = this.editingEntryDate;
      } else {
        delete updatedEntry.expires_at;
      }

      const normalizedLabel = this.normalizeLabelValue(this.editingEntryLabel);
      if (normalizedLabel) {
        updatedEntry.remove_labels = [normalizedLabel];
      } else {
        delete updatedEntry.remove_labels;
      }

      const normalizedAddLabel = this.normalizeLabelValue(
        this.editingEntryAddLabel
      );
      if (normalizedAddLabel) {
        updatedEntry.add_labels = [normalizedAddLabel];
      } else {
        delete updatedEntry.add_labels;
      }

      const updated = this.entries.map((entry, idx) =>
        idx === index ? updatedEntry : entry
      );
      this.$emit('update', this.attributeKey, updated);
      this.cancelEditEntry();
    },
    cancelEditEntry() {
      this.editingEntryIndex = null;
      this.editingEntryText = '';
      this.editingEntryDate = '';
      this.editingEntryLabel = '';
      this.editingEntryAddLabel = '';
    },
    cancelAddEntry() {
      this.newEntryText = '';
      this.newEntryDate = '';
      this.newEntryLabel = '';
      this.newEntryAddLabel = '';
      this.showLabelPicker = false;
      this.showAddEntryForm = false;
    },
  },
};
</script>

<template>
  <div class="px-4 py-3">
    <div class="flex items-center mb-1">
      <h4 class="flex items-center w-full m-0 text-sm error">
        <div v-if="isAttributeTypeCheckbox" class="flex items-center">
          <input
            v-model="editedValue"
            class="!my-0 ltr:mr-2 ltr:ml-0 rtl:mr-0 rtl:ml-2"
            type="checkbox"
            @change="onUpdate"
          />
        </div>
        <div class="flex items-center justify-between w-full">
          <span
            class="w-full inline-flex gap-1.5 items-start font-medium whitespace-nowrap text-sm mb-0"
            :class="
              v$.editedValue.$error ? 'text-n-ruby-11' : 'text-n-slate-12'
            "
          >
            {{ label }}
            <HelperTextPopup
              v-if="description"
              :message="description"
              class="mt-0.5"
            />
          </span>
          <NextButton
            v-if="showActions && hasValue"
            v-tooltip.left="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
            slate
            sm
            link
            icon="i-lucide-trash-2"
            @click="onDelete"
          />
        </div>
      </h4>
    </div>
    <div v-if="notAttributeTypeCheckboxAndList">
      <div v-if="isEditing" v-on-clickaway="onClickAway">
        <div class="flex items-center w-full mb-2">
          <input
            ref="inputfield"
            v-model="editedValue"
            :type="inputType"
            class="!h-8 ltr:!rounded-r-none rtl:!rounded-l-none !mb-0 !text-sm"
            autofocus="true"
            :class="{ error: v$.editedValue.$error }"
            @blur="v$.editedValue.$touch"
            @keyup.enter="onUpdate"
          />
          <div>
            <NextButton
              sm
              icon="i-lucide-check"
              class="ltr:rounded-l-none rtl:rounded-r-none h-[34px]"
              @click="onUpdate"
            />
          </div>
        </div>
        <span
          v-if="shouldShowErrorMessage"
          class="block w-full -mt-px text-sm font-normal text-n-ruby-11"
        >
          {{ errorMessage }}
        </span>
      </div>
      <div
        v-show="!isEditing"
        class="flex group"
        :class="{ 'is-editable': showActions }"
      >
        <a
          v-if="isAttributeTypeLink"
          :href="hrefURL"
          target="_blank"
          rel="noopener noreferrer"
          class="group-hover:bg-n-slate-3 group-hover:dark:bg-n-solid-3 inline-block rounded-sm mb-0 break-all py-0.5 px-1"
        >
          {{ urlValue }}
        </a>
        <p
          v-else
          class="group-hover:bg-n-slate-3 group-hover:dark:bg-n-solid-3 inline-block rounded-sm mb-0 break-all py-0.5 px-1"
        >
          {{ displayValue }}
        </p>
        <div
          class="flex items-center max-w-[2rem] gap-1 ml-1 rtl:mr-1 rtl:ml-0"
        >
          <NextButton
            v-if="showActions && hasValue"
            v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
            xs
            slate
            ghost
            icon="i-lucide-clipboard"
            class="hidden group-hover:flex flex-shrink-0"
            @click="onCopy"
          />
          <NextButton
            v-if="showActions"
            v-tooltip.right="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
            xs
            slate
            ghost
            icon="i-lucide-pen"
            class="hidden group-hover:flex flex-shrink-0"
            @click="onEdit"
          />
        </div>
      </div>
    </div>
    <div v-if="isAttributeTypeList">
      <MultiselectDropdown
        :options="listOptions"
        :selected-item="selectedItem"
        :has-thumbnail="false"
        :multiselector-placeholder="
          $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.PLACEHOLDER')
        "
        :no-search-result="
          $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT')
        "
        :input-placeholder="
          $t(
            'CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.SEARCH_INPUT_PLACEHOLDER'
          )
        "
        @select="onUpdateListValue"
      />
    </div>

    <!-- Multi-entry type: list of entries each with optional expiry date -->
    <div v-if="isAttributeTypeMultiEntry" class="space-y-1">
      <div
        v-for="(entry, idx) in entries"
        :key="idx"
        class="flex items-start gap-1.5 group/entry"
      >
        <div class="flex-1 min-w-0">
          <div v-if="editingEntryIndex === idx" class="space-y-1.5">
            <input
              v-model="editingEntryText"
              type="text"
              class="!h-8 !text-sm !mb-0 w-full"
              :placeholder="
                $t('CUSTOM_ATTRIBUTES.MULTI_ENTRY.ENTRY_PLACEHOLDER')
              "
              @keyup.enter="saveEditedEntry(idx)"
            />
            <div class="flex items-center gap-2">
              <span
                class="i-lucide-clock size-4 text-n-slate-9 flex-shrink-0"
              />
              <input
                v-model="editingEntryDate"
                type="date"
                class="flex-1 h-8 px-2 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border border-n-slate-6 outline-none"
              />
            </div>
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-tag size-4 text-n-slate-9 flex-shrink-0" />
              <select
                v-model="editingEntryLabel"
                class="flex-1 h-8 px-2 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border border-n-slate-6 outline-none"
              >
                <option value="">
                  {{ $t('CUSTOM_ATTRIBUTES.MULTI_ENTRY.NO_LABEL') }}
                </option>
                <option
                  v-if="
                    editingEntryLabel &&
                    !accountLabels.some(
                      label => label.title === editingEntryLabel
                    )
                  "
                  :value="editingEntryLabel"
                >
                  {{ editingEntryLabel }}
                </option>
                <option
                  v-for="label in accountLabels"
                  :key="label.id"
                  :value="label.title"
                >
                  {{ label.title }}
                </option>
              </select>
            </div>
            <div class="flex items-center gap-1.5">
              <span
                class="i-lucide-arrow-right-left size-4 text-n-slate-9 flex-shrink-0"
              />
              <select
                v-model="editingEntryAddLabel"
                class="flex-1 h-8 px-2 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border border-n-slate-6 outline-none"
              >
                <option value="">
                  {{ $t('CUSTOM_ATTRIBUTES.MULTI_ENTRY.NO_ADD_LABEL') }}
                </option>
                <option
                  v-if="
                    editingEntryAddLabel &&
                    !accountLabels.some(
                      label => label.title === editingEntryAddLabel
                    )
                  "
                  :value="editingEntryAddLabel"
                >
                  {{ editingEntryAddLabel }}
                </option>
                <option
                  v-for="label in accountLabels"
                  :key="`add-${label.id}`"
                  :value="label.title"
                >
                  {{ label.title }}
                </option>
              </select>
            </div>
            <div class="flex items-center justify-end gap-2">
              <button
                class="flex-shrink-0 flex items-center justify-center size-8 rounded bg-n-blue-9 text-white hover:bg-n-blue-10"
                @click="saveEditedEntry(idx)"
              >
                <span class="i-lucide-check size-5" />
              </button>
              <button
                class="flex-shrink-0 flex items-center justify-center size-8 rounded text-n-slate-10 hover:bg-n-alpha-black2"
                @click="cancelEditEntry"
              >
                <span class="i-lucide-x size-5" />
              </button>
            </div>
          </div>
          <div v-else>
            <p class="text-sm text-n-slate-12 mb-0 break-words">
              {{ entry.text }}
            </p>
            <span
              v-if="entry.expires_at"
              class="text-xs"
              :class="
                new Date(entry.expires_at) <= new Date()
                  ? 'text-n-ruby-9'
                  : 'text-n-amber-9'
              "
            >
              {{
                $t('CUSTOM_ATTRIBUTES.MULTI_ENTRY.EXPIRES_ON', {
                  date: formatEntryDate(entry.expires_at),
                })
              }}
            </span>
            <span
              v-if="entry.remove_labels && entry.remove_labels.length"
              class="flex items-center gap-1 text-xs text-n-slate-9 mt-1"
            >
              <span class="i-lucide-tag size-3 flex-shrink-0" />
              {{
                $t('CUSTOM_ATTRIBUTES.MULTI_ENTRY.REMOVE_LABEL_ON_EXPIRE', {
                  label: entry.remove_labels.join(', '),
                })
              }}
            </span>
            <span
              v-if="entry.add_labels && entry.add_labels.length"
              class="flex items-center gap-1 text-xs text-n-slate-9 mt-1"
            >
              <span class="i-lucide-tag size-3 flex-shrink-0" />
              {{
                $t('CUSTOM_ATTRIBUTES.MULTI_ENTRY.ADD_LABEL_ON_EXPIRE', {
                  label: entry.add_labels.join(', '),
                })
              }}
            </span>
          </div>
        </div>
        <div
          v-if="showActions && editingEntryIndex !== idx"
          class="hidden group-hover/entry:flex items-center gap-1 flex-shrink-0 mt-0.5"
        >
          <NextButton
            v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
            xs
            slate
            ghost
            icon="i-lucide-pen"
            @click="startEditEntry(entry, idx)"
          />
          <NextButton
            v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
            xs
            slate
            ghost
            icon="i-lucide-x"
            @click="removeEntry(idx)"
          />
        </div>
      </div>
      <div v-if="entries.length === 0" class="text-sm text-n-slate-9">---</div>
      <!-- Add entry form -->
      <div v-if="showActions && showAddEntryForm" class="mt-2 space-y-1.5">
        <input
          v-model="newEntryText"
          type="text"
          class="!h-8 !text-sm !mb-0 w-full"
          :placeholder="$t('CUSTOM_ATTRIBUTES.MULTI_ENTRY.ENTRY_PLACEHOLDER')"
          @keyup.enter="addEntry"
        />
        <div class="flex items-center gap-2">
          <span class="i-lucide-clock size-4 text-n-slate-9 flex-shrink-0" />
          <input
            v-model="newEntryDate"
            type="date"
            class="flex-1 h-8 px-2 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border border-n-slate-6 outline-none"
          />
          <button
            class="flex-shrink-0 flex items-center justify-center size-8 rounded bg-n-blue-9 text-white hover:bg-n-blue-10"
            @click="addEntry"
          >
            <span class="i-lucide-check size-5" />
          </button>
          <button
            class="flex-shrink-0 flex items-center justify-center size-8 rounded text-n-slate-10 hover:bg-n-alpha-black2"
            @click="cancelAddEntry"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>
        <!-- Etiqueta de conversación a quitar al vencer -->
        <div class="flex items-center gap-1.5">
          <span class="i-lucide-tag size-4 text-n-slate-9 flex-shrink-0" />
          <select
            v-model="newEntryLabel"
            class="flex-1 h-8 px-2 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border border-n-slate-6 outline-none"
          >
            <option value="">
              {{ $t('CUSTOM_ATTRIBUTES.MULTI_ENTRY.NO_LABEL') }}
            </option>
            <option
              v-for="label in accountLabels"
              :key="label.id"
              :value="label.title"
            >
              {{ label.title }}
            </option>
          </select>
        </div>
        <div class="flex items-center gap-1.5">
          <span
            class="i-lucide-arrow-right-left size-4 text-n-slate-9 flex-shrink-0"
          />
          <select
            v-model="newEntryAddLabel"
            class="flex-1 h-8 px-2 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border border-n-slate-6 outline-none"
          >
            <option value="">
              {{ $t('CUSTOM_ATTRIBUTES.MULTI_ENTRY.NO_ADD_LABEL') }}
            </option>
            <option
              v-for="label in accountLabels"
              :key="`new-add-${label.id}`"
              :value="label.title"
            >
              {{ label.title }}
            </option>
          </select>
        </div>
      </div>
      <button
        v-if="showActions && !showAddEntryForm && editingEntryIndex === null"
        class="text-xs flex items-center gap-1 text-n-slate-9 hover:text-n-slate-12 hover:underline mt-1"
        @click="showAddEntryForm = true"
      >
        <span class="i-lucide-plus size-3" />
        {{ $t('CUSTOM_ATTRIBUTES.MULTI_ENTRY.ADD_ENTRY') }}
      </button>
    </div>

    <!-- Expiry row: solo para atributos de contacto con showActions (no aplica a multi_entry) -->
    <div
      v-if="showActions && contactId && !isAttributeTypeMultiEntry"
      class="mt-1"
    >
      <button
        v-if="!showExpiryPicker"
        class="text-xs flex items-center gap-1 hover:underline"
        :class="formattedExpiry ? 'text-n-amber-9' : 'text-n-slate-9'"
        @click="toggleExpiryPicker"
      >
        <span class="i-lucide-clock size-3" />
        <span>{{
          formattedExpiry
            ? $t('CUSTOM_ATTRIBUTES.EXPIRY.EXPIRES_ON', {
                date: formattedExpiry,
              })
            : $t('CUSTOM_ATTRIBUTES.EXPIRY.ADD')
        }}</span>
      </button>
      <div v-else class="flex items-center gap-2 mt-1">
        <span class="i-lucide-clock size-4 text-n-slate-10 flex-shrink-0" />
        <input
          v-model="expiryDateInput"
          type="date"
          class="flex-1 h-8 px-2 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border border-n-slate-6 outline-none"
        />
        <button
          class="flex-shrink-0 flex items-center justify-center size-8 rounded bg-n-blue-9 text-white hover:bg-n-blue-10"
          @click="onSaveExpiry"
        >
          <span class="i-lucide-check size-5" />
        </button>
        <button
          v-if="expiration"
          class="flex-shrink-0 flex items-center justify-center size-8 rounded bg-n-ruby-3 text-n-ruby-9 hover:bg-n-ruby-4"
          @click="onDeleteExpiry"
        >
          <span class="i-lucide-x size-5" />
        </button>
        <button
          class="flex-shrink-0 flex items-center justify-center size-8 rounded text-n-slate-10 hover:bg-n-alpha-black2"
          @click="showExpiryPicker = false"
        >
          <span class="i-lucide-chevron-up size-5" />
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .selector-wrap {
    @apply m-0 top-1;

    .selector-name {
      @apply ml-0;
    }
  }

  .name {
    @apply ml-0;
  }
}
</style>
